import { useState, useCallback } from 'react'
import type { MediaItem } from '../types'
import { supabase } from '../lib/supabase'

const MAX_MEDIA  = 5
const MAX_IMG_MB = 5
const MAX_VID_MB = 50

export function useMediaUpload(initial: MediaItem[] = []) {
  const [media, setMedia] = useState<MediaItem[]>(initial)
  const [urlError, setUrlError] = useState('')
  const [isUploading, setIsUploading] = useState(false)

  const addMedia = useCallback((item: MediaItem) => {
    setMedia(prev => {
      if (prev.length >= MAX_MEDIA) return prev
      if (prev.some(m => m.src === item.src)) return prev
      if (item.type === 'video' && prev.length === 0) {
        setUrlError('וידאו לא יכול להיות פריט ראשון — הוסף תמונה קודם')
        return prev
      }
      return [...prev, item]
    })
  }, [])

  const removeMedia = useCallback((idx: number) => {
    setMedia(prev => {
      const next = prev.filter((_, i) => i !== idx)
      if (next.length > 0 && next[0].type === 'video') {
        setUrlError('שים לב: וידאו הוא כרגע הפריט הראשון — מומלץ להוסיף תמונה לפניו')
      }
      return next
    })
  }, [])

  const moveMedia = useCallback((from: number, to: number) => {
    setMedia(prev => {
      if (to < 0 || to >= prev.length) return prev
      const arr = [...prev]
      const [item] = arr.splice(from, 1)
      arr.splice(to, 0, item)
      if (arr[0].type === 'video') {
        setUrlError('שים לב: וידאו הוא כרגע הפריט הראשון — מומלץ להוסיף תמונה לפניו')
      }
      return arr
    })
  }, [])

  const uploadFile = useCallback(async (file: File): Promise<MediaItem> => {
    const isVideo = file.type.startsWith('video/')
    const isImage = file.type.startsWith('image/')
    if (!isImage && !isVideo) throw new Error('הקובץ חייב להיות תמונה או וידאו')
    const maxMB = isVideo ? MAX_VID_MB : MAX_IMG_MB
    if (file.size > maxMB * 1024 * 1024) throw new Error(`גודל מקסימלי: ${maxMB}MB`)
  
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) throw new Error('לא מחובר')
  
    const bucket = isVideo ? 'product-videos' : 'product-images'
    const ext = file.name.split('.').pop()
    const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`
  
    const { error } = await supabase.storage
      .from(bucket)
      .upload(path, file, { contentType: file.type, upsert: false })
  
    if (error) throw new Error(error.message)
  
    const { data: { publicUrl } } = supabase.storage
      .from(bucket)
      .getPublicUrl(path)
  
    return { src: publicUrl, type: isVideo ? 'video' : 'image' }
  }, [])

  const handleFiles = useCallback(async (files: FileList | null) => {
    if (!files) return
    const remaining = MAX_MEDIA - media.length
    if (remaining <= 0) return
    const toProcess = Array.from(files).slice(0, remaining)
    setIsUploading(true)
    for (const file of toProcess) {
      try {
        if (file.type.startsWith('video/') && media.length === 0) {
          setUrlError('וידאו לא יכול להיות פריט ראשון — הוסף תמונה קודם')
          continue
        }
        const item = await uploadFile(file)
        addMedia(item)
      } catch (e: unknown) {
        setUrlError((e as Error).message)
      }
    }
    setIsUploading(false)
  }, [media.length, uploadFile, addMedia])

  const addFromUrl = useCallback((url: string): string | null => {
    const trimmed = url.trim()
    if (!trimmed) return 'הכנס כתובת URL'
    try { new URL(trimmed) } catch { return 'URL לא תקין' }
    if (media.some(m => m.src === trimmed)) return 'מדיה זו כבר קיימת'
    const isVideo = /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(trimmed)
    addMedia({ src: trimmed, type: isVideo ? 'video' : 'image' })
    return null
  }, [media, addMedia])

  return {
    media,
    urlError, setUrlError,
    isUploading,
    addMedia,
    removeMedia,
    moveMedia,
    handleFiles,
    addFromUrl,
    MAX_MEDIA,
    MAX_IMG_MB,
    MAX_VID_MB,
    canAddMore: media.length < MAX_MEDIA,
  }
}